import React, { useEffect, useMemo, useState } from 'react';
import { SparkModalPortal, Modal, DropdownMenu, Text, Button, Divider, Spinner, SparkIcon } from '@spark';
import {
  CONCURRENCY_OPTIONS,
  LENGTH_CATEGORY_OPTIONS,
  getRecommendedLengthCategory,
  CLOUD_PROVIDER_OPTIONS,
  HARDWARE_DEVICE_OPTIONS,
  fetchPerformanceMetricsOptions,
  STATIC_FALLBACK_CATALOG,
} from './data/performanceMetricsApi';
import {
  resolveAvailableEnvironment,
  reconcileEnvWithCatalog,
  availableCloudProviders,
  availableHardwareDevices,
  availableAcceleratorOptions,
  availableInstanceTypeOptions,
} from './data/filterReconciliation';

// Hard cap on the number of environments the user can compare side-by-side.
// Mirrors the rankColors palette (which only supports up to 4 distinct
// rows) and keeps the table readable.
export const MAX_ENVIRONMENTS = 4;

/**
 * Filter Performance Metrics modal — controls Concurrency, Length and
 * one or more Infrastructure environments (cloud provider, hardware
 * device, accelerator, instance type).
 *
 * Catalog & concurrency options are fetched from
 * `GET /performance-metrics/options?base_model_name=...` so the user
 * can only pick combinations actually seeded in `performance_metrics`.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {string|null} [props.baseModelName] - Drives which catalog
 *        is fetched. When null/empty, the modal falls back to the
 *        static default catalog (filter is effectively read-only).
 * @param {() => void} props.onCancel
 * @param {(filters: import('./data/performanceMetricsApi').PerformanceFilters) => void} props.onApply
 * @param {import('./data/performanceMetricsApi').PerformanceFilters} props.value
 * @param {number|null} [props.benchmarkAvgTiktoken] - Mean
 *        `request_example_summary_data.num_tiktoken.avg` for the usecase
 *        benchmark(s); drives the Recommended label on Short/Long.
 */
export default function PerformanceFiltersModal({
  open,
  baseModelName = null,
  benchmarkAvgTiktoken = null,
  onCancel,
  onApply,
  value,
}) {
  const [draft, setDraft] = useState(value);
  const [catalog, setCatalog] = useState(STATIC_FALLBACK_CATALOG);
  const [concurrencyLevels, setConcurrencyLevels] = useState([]);
  const [lengthCategories, setLengthCategories] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  // Only flips to true after the user clicks "Add Infrastructure" while
  // already at MAX_ENVIRONMENTS — that's when we surface the red message
  // and disable the button. Reset whenever the modal re-opens or the
  // environment count drops back below the cap.
  const [showCapMessage, setShowCapMessage] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(value);
      setShowCapMessage(false);
    }
  }, [open, value]);

  // Fetch the live catalog when the modal opens for a given base model.
  useEffect(() => {
    if (!open || !baseModelName) return undefined;
    let cancelled = false;
    setOptionsLoading(true);
    fetchPerformanceMetricsOptions({ baseModelName })
      .then((resp) => {
        if (cancelled) return;
        const liveCatalog =
          resp?.machineCatalog && Object.keys(resp.machineCatalog).length
            ? resp.machineCatalog
            : STATIC_FALLBACK_CATALOG;
        setCatalog(liveCatalog);
        setConcurrencyLevels(Array.isArray(resp?.concurrencyLevels) ? resp.concurrencyLevels : []);
        setLengthCategories(Array.isArray(resp?.lengthCategories) ? resp.lengthCategories : []);
        // Reconcile draft so it never points at an unseeded combo.
        setDraft((d) => ({
          ...d,
          environments: (d.environments || []).map((env) =>
            reconcileEnvWithCatalog(env, liveCatalog)
          ),
        }));
      })
      .catch(() => {
        if (cancelled) return;
        setCatalog(STATIC_FALLBACK_CATALOG);
        setConcurrencyLevels([]);
        setLengthCategories([]);
      })
      .finally(() => {
        if (!cancelled) setOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, baseModelName]);

  // Concurrency options — restrict to seeded levels when known, otherwise
  // fall back to the static defaults so the Select still renders. Labels are
  // wrapped in EllipsisTextWithTooltip so long names (e.g. "Massive
  // Throughput (1,000 concurrent requests)") truncate gracefully and reveal
  // the full text on hover, both inside the dropdown and in the selected
  // value display.
  const concurrencyOptions = useMemo(() => {
    const toOption = (val, label) => ({ id: String(val), label });
    if (!concurrencyLevels.length) {
      return CONCURRENCY_OPTIONS.map((o) => toOption(o.value, o.label));
    }
    const labelByValue = new Map(CONCURRENCY_OPTIONS.map((o) => [o.value, o.label]));
    return concurrencyLevels.map((val) =>
      toOption(val, labelByValue.get(val) || `${val} concurrent requests`)
    );
  }, [concurrencyLevels]);

  const recommendedLengthCategory = useMemo(
    () => getRecommendedLengthCategory(benchmarkAvgTiktoken),
    [benchmarkAvgTiktoken]
  );

  // Length options — restrict to seeded categories when known.
  const lengthOptions = useMemo(() => {
    const base = !lengthCategories.length
      ? LENGTH_CATEGORY_OPTIONS
      : LENGTH_CATEGORY_OPTIONS.filter((o) => new Set(lengthCategories).has(o.value));
    return base.map((o) => ({
      id: o.value,
      label: recommendedLengthCategory === o.value ? `${o.label} (Recommended)` : o.label,
    }));
  }, [lengthCategories, recommendedLengthCategory]);

  const updateEnv = (idx, patch) => {
    setDraft((d) => ({
      ...d,
      environments: d.environments.map((env, i) => (i === idx ? { ...env, ...patch } : env)),
    }));
  };

  // Sibling environments for row `idx`. Their (cloud, hardware, accelerator,
  // instance) tuples are the ones this row must avoid so two rows never
  // resolve to the same infrastructure (which would be a redundant
  // comparison). We hide any option that would only lead to a taken tuple.
  const otherEnvsFor = (idx) => (draft.environments || []).filter((_, i) => i !== idx);

  // Cloud options for a row — only clouds that still have at least one
  // (hardware, accelerator, instance) tuple not already taken by a sibling.
  const cloudOptionsFor = (idx) => {
    const allowed = new Set(availableCloudProviders(catalog, otherEnvsFor(idx)));
    return CLOUD_PROVIDER_OPTIONS.filter((o) => allowed.has(o.value));
  };

  // Hardware options for a row — only devices that still have an unused tuple.
  const hardwareOptionsFor = (idx, env) => {
    const allowed = new Set(
      availableHardwareDevices(catalog, env.cloudProvider, otherEnvsFor(idx))
    );
    return HARDWARE_DEVICE_OPTIONS.filter((o) => allowed.has(o.value));
  };

  // Reselect downstream fields whenever an upstream field changes so the
  // draft always references a seeded, non-duplicate combo.
  const handleCloudChange = (idx, nextCloud) => {
    updateEnv(
      idx,
      resolveAvailableEnvironment({ cloudProvider: nextCloud }, catalog, otherEnvsFor(idx))
    );
  };

  const handleHardwareChange = (idx, nextHardware, env) => {
    updateEnv(
      idx,
      resolveAvailableEnvironment(
        { ...env, hardwareDevice: nextHardware },
        catalog,
        otherEnvsFor(idx)
      )
    );
  };

  const handleAcceleratorChange = (idx, nextAccelerator, env) => {
    updateEnv(
      idx,
      resolveAvailableEnvironment(
        { ...env, accelerator: nextAccelerator },
        catalog,
        otherEnvsFor(idx)
      )
    );
  };

  const addEnv = () => {
    setDraft((d) => {
      if ((d.environments?.length || 0) >= MAX_ENVIRONMENTS) {
        setShowCapMessage(true);
        return d;
      }
      return {
        ...d,
        environments: [
          ...d.environments,
          resolveAvailableEnvironment({}, catalog, d.environments),
        ],
      };
    });
  };

  const removeEnv = (idx) => {
    setDraft((d) => ({
      ...d,
      environments: d.environments.filter((_, i) => i !== idx),
    }));
    setShowCapMessage(false);
  };

  const handleDone = () => {
    onApply?.(draft);
  };

  const showRemove = (draft.environments?.length || 0) > 1;
  const showCardBorders = (draft.environments?.length || 0) > 1;
  const atEnvironmentCap = (draft.environments?.length || 0) >= MAX_ENVIRONMENTS;
  // Disable the button + show the red message only after the user has
  // tried to add past the cap (per UX spec).
  const blockAddInfrastructure = atEnvironmentCap && showCapMessage;

  // Convert AntD-style {value,label} options to DropdownMenu {id,label}
  const toDropdownItems = (opts) => (opts || []).map((o) => ({ id: String(o.value), label: o.label }));

  // Spark DropdownMenu renders the selected value by label string.
  const labelForId = (items, id) =>
    (items || []).find((item) => item.id === String(id ?? ''))?.label;

  const twoColumnRowStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 'var(--dimension-spacing-24, 24px)',
    rowGap: 'var(--dimension-spacing-16, 16px)',
  };

  // 800px keeps each field column ≥363px so the widest concurrency option
  // ("Massive Throughput (1,000 concurrent requests)") fits untruncated in
  // the dropdown panel, which matches the trigger width.
  return (
    <SparkModalPortal open={open} onClose={onCancel} width={800} verticalAlign="top">
      <Modal
        title="Filter Performance Metrics"
        subtitle="Select the request configuration and one or more infrastructure environments to compare."
        onCloseClick={onCancel}
        tertiaryLabel="Cancel"
        primaryLabel="Done"
        onTertiaryClick={onCancel}
        onPrimaryClick={handleDone}
      >
        {optionsLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 16 }}>
            <Spinner size="small" />
          </div>
        )}

        <Text variant="base-semibold" color="primary" as="div">
          Request configuration
        </Text>
        <div style={{ ...twoColumnRowStyle, marginTop: 16 }}>
          <DropdownMenu
            fill
            label="Concurrency"
            items={concurrencyOptions}
            value={labelForId(concurrencyOptions, draft.concurrency)}
            onSelect={(item) => setDraft((d) => ({ ...d, concurrency: Number(item.id) || item.id }))}
          />
          <DropdownMenu
            fill
            label="Length"
            items={lengthOptions}
            value={labelForId(lengthOptions, draft.lengthCategory)}
            onSelect={(item) => setDraft((d) => ({ ...d, lengthCategory: item.id }))}
          />
        </div>

        <div style={{ margin: '24px 0' }}>
          <Divider />
        </div>

        <Text variant="base-semibold" color="primary" as="div">
          Infrastructure
        </Text>
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {draft.environments.map((env, idx) => {
            const cloudItems = toDropdownItems(cloudOptionsFor(idx));
            const hardwareItems = toDropdownItems(hardwareOptionsFor(idx, env));
            const acceleratorItems = toDropdownItems(availableAcceleratorOptions(
              catalog,
              env.cloudProvider,
              env.hardwareDevice,
              otherEnvsFor(idx),
            ));
            const instanceItems = toDropdownItems(availableInstanceTypeOptions(
              catalog,
              env.cloudProvider,
              env.hardwareDevice,
              env.accelerator,
              otherEnvsFor(idx),
            ));
            return (
              <div
                key={idx}
                style={
                  showCardBorders
                    ? {
                        border: '1px solid var(--semantic-border-default)',
                        borderRadius: 'var(--dimension-border-radius-08, 8px)',
                        padding: 'var(--dimension-spacing-12, 12px)',
                      }
                    : undefined
                }
              >
                <div style={twoColumnRowStyle}>
                  <DropdownMenu
                    fill
                    label="Cloud provider"
                    items={cloudItems}
                    value={labelForId(cloudItems, env.cloudProvider)}
                    onSelect={(item) => handleCloudChange(idx, item.id)}
                  />
                  <DropdownMenu
                    fill
                    label="Hardware device"
                    items={hardwareItems}
                    value={labelForId(hardwareItems, env.hardwareDevice)}
                    onSelect={(item) => handleHardwareChange(idx, item.id, env)}
                  />
                  <DropdownMenu
                    fill
                    label="Accelerator"
                    items={acceleratorItems}
                    value={labelForId(acceleratorItems, env.accelerator)}
                    onSelect={(item) => handleAcceleratorChange(idx, item.id, env)}
                  />
                  <DropdownMenu
                    fill
                    label="Instance type"
                    items={instanceItems}
                    value={labelForId(instanceItems, env.instanceType)}
                    onSelect={(item) => updateEnv(idx, { instanceType: item.id })}
                  />
                </div>
                {showRemove && (
                  <div style={{ textAlign: 'right', marginTop: 8 }}>
                    <Button variant="ghost" danger size="small" onClick={() => removeEnv(idx)}>
                      Remove environment
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 16 }}>
          <Button
            variant="ghost"
            startIcon={<SparkIcon slug="plus" />}
            onClick={addEnv}
            disabled={blockAddInfrastructure}
          >
            Add Infrastructure
          </Button>
          {blockAddInfrastructure && (
            <Text
              variant="base-regular"
              as="div"
              style={{ marginTop: 8, color: 'var(--semantic-feedback-critical-on-surface)' }}
            >
              You can compare up to {MAX_ENVIRONMENTS} infrastructures. Remove one to add another.
            </Text>
          )}
        </div>

      </Modal>
    </SparkModalPortal>
  );
}
